import { Component, type ReactNode } from 'react'

type Props = { children: ReactNode }
type State = { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    console.error('App crashed', error, info)
  }

  render() {
    if (this.state.error) {
      const msg = this.state.error.message
      return (
        <div className="grid min-h-screen place-items-center p-6">
          <div className="glass-strong w-full max-w-xl p-6">
            <h2 className="mb-2 text-lg font-bold">Something just broke. Sorry.</h2>
            <p className="mb-3 text-sm text-ink-200">
              The UI crashed while rendering. Most often this is a too-many-DOM-nodes
              issue after a very large feed pull. Your IndexedDB cache is intact.
            </p>
            <pre className="mb-4 max-h-40 overflow-auto rounded-lg bg-black/40 p-3 text-[11px] text-rose-200">
              {msg}
            </pre>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="glass-button glass-button--accent"
                onClick={() => location.reload()}
              >
                reload page
              </button>
              <button
                type="button"
                className="glass-button"
                onClick={() => {
                  location.assign('/')
                }}
              >
                go home
              </button>
              <button
                type="button"
                className="glass-button text-xs"
                onClick={async () => {
                  if (
                    confirm(
                      'Wipe ALL local cache (records, hydrated posts, profiles, gallery)? This cannot be undone.',
                    )
                  ) {
                    const dbs = await indexedDB.databases?.()
                    if (dbs) {
                      for (const d of dbs) if (d.name) indexedDB.deleteDatabase(d.name)
                    } else {
                      indexedDB.deleteDatabase('bskydiscombobulate')
                    }
                    location.reload()
                  }
                }}
              >
                wipe local cache + reload
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
